import { applyHelmertTransform } from '@/logic/adjustments';
import { getWeightStatus, type WeightStatus } from '@/logic/weights';
import { isCoordinate } from '@/utils/fileParser';
import type {
  PointPair,
  PointResidual,
  SystemPoint,
  TransformationSolution,
} from '@/types';
import { computeBounds, type Bounds, type GroundPoint } from './geometry';

export type MapSystem = 1 | 2;
export type PointKind = 'pair' | 'transformed' | 'unmatched';

/** One drawable, focusable point in a pane. */
export interface MapPoint {
  /** Unique within its pane. */
  key: string;
  kind: PointKind;
  /** Pair id for paired and transformed points. */
  pairId: number | null;
  /** Dataset the identifier belongs to (1 Record, 2 Field). */
  system: MapSystem;
  label: string;
  n: number;
  e: number;
  /** Weight status of the pair; null for unmatched points. */
  status: WeightStatus | null;
  flagged: boolean;
  ariaLabel: string;
}

export const systemName = (system: MapSystem): 'Record' | 'Field' =>
  system === 1 ? 'Record' : 'Field';

const parsePoint = (point: SystemPoint): GroundPoint | null => {
  const n = parseFloat(point.north);
  const e = parseFloat(point.east);
  return Number.isFinite(n) && Number.isFinite(e) ? { n, e } : null;
};

/** Pairs whose four coordinates are numeric. */
export const completePairs = (pairs: readonly PointPair[]): PointPair[] =>
  pairs.filter((p) => [p.n1, p.e1, p.n2, p.e2].every(isCoordinate));

/** Imported points that no pair uses (O(n) with a Set). */
export const unmatchedPoints = (
  points: readonly SystemPoint[] | undefined,
  pairs: readonly PointPair[],
  system: MapSystem
): SystemPoint[] => {
  if (!points) return [];
  const used = new Set(
    pairs.map((pair) => (system === 1 ? pair.sys1Point : pair.sys2Point))
  );
  return points.filter((point) => !used.has(point.point));
};

/** Data bounds of each dataset, including unmatched points. */
export const datasetBounds = (
  pairs: readonly PointPair[],
  unmatched1: readonly SystemPoint[],
  unmatched2: readonly SystemPoint[]
): { sys1: Bounds | null; sys2: Bounds | null } | null => {
  const ground = (points: readonly SystemPoint[]) =>
    points.flatMap((point) => parsePoint(point) ?? []);
  const sys1 = computeBounds([
    ...pairs.map((p) => ({ n: parseFloat(p.n1), e: parseFloat(p.e1) })),
    ...ground(unmatched1),
  ]);
  const sys2 = computeBounds([
    ...pairs.map((p) => ({ n: parseFloat(p.n2), e: parseFloat(p.e2) })),
    ...ground(unmatched2),
  ]);
  return sys1 || sys2 ? { sys1, sys2 } : null;
};

/** Residuals by pair id for O(1) lookup. */
export const residualIndex = (
  solution: TransformationSolution | null | undefined
): ReadonlyMap<number, PointResidual> =>
  new Map(
    solution && !solution.error
      ? solution.residuals.map((residual) => [residual.id, residual])
      : []
  );

const unmatchedMapPoints = (
  points: readonly SystemPoint[],
  system: MapSystem,
  position: (point: GroundPoint) => GroundPoint = (point) => point
): MapPoint[] =>
  points.flatMap((point, index) => {
    const ground = parsePoint(point);
    if (!ground) return [];
    const { n, e } = position(ground);
    return [
      {
        key: `u${system}:${index}:${point.point}`,
        kind: 'unmatched' as const,
        pairId: null,
        system,
        label: point.point,
        n,
        e,
        status: null,
        flagged: false,
        ariaLabel: `Link ${systemName(system)} point ${point.point}`,
      },
    ];
  });

/** Points of a Record or Field pane in their own coordinates. */
export const sourcePanePoints = (
  system: MapSystem,
  pairs: readonly PointPair[],
  unmatched: readonly SystemPoint[],
  flaggedIds: ReadonlySet<number>
): MapPoint[] => [
  ...unmatchedMapPoints(unmatched, system),
  ...pairs.map((pair) => {
    const label = system === 1 ? pair.sys1Point : pair.sys2Point;
    return {
      key: `p${pair.id}`,
      kind: 'pair' as const,
      pairId: pair.id,
      system,
      label,
      n: parseFloat(system === 1 ? pair.n1 : pair.n2),
      e: parseFloat(system === 1 ? pair.e1 : pair.e2),
      status: getWeightStatus(pair.weight),
      flagged: flaggedIds.has(pair.id),
      ariaLabel: `Select ${systemName(system)} point ${label}`,
    };
  }),
];

export interface TransformedPosition {
  id: number;
  point: string;
  n: number;
  e: number;
}

/**
 * Points of the fit pane: observed Field points, the fit's transformed Record
 * positions, unmatched Field points, and (with a fit) unmatched Record points
 * transformed into the Field frame.
 */
export const fitPanePoints = (
  pairs: readonly PointPair[],
  transformed: readonly TransformedPosition[],
  unmatchedRecord: readonly SystemPoint[],
  unmatchedField: readonly SystemPoint[],
  solution: TransformationSolution | null | undefined,
  flaggedIds: ReadonlySet<number>
): MapPoint[] => {
  const pairById = new Map(pairs.map((pair) => [pair.id, pair]));
  const fit = solution && !solution.error ? solution : null;
  return [
    ...unmatchedMapPoints(unmatchedField, 2),
    ...(fit
      ? unmatchedMapPoints(unmatchedRecord, 1, ({ n, e }) => {
          const t = applyHelmertTransform(n, e, fit);
          return { n: t.n2, e: t.e2 };
        })
      : []),
    ...sourcePanePoints(2, pairs, [], flaggedIds),
    // Transformed Record positions draw on top of their Field observations.
    ...transformed.flatMap((position) => {
      const pair = pairById.get(position.id);
      if (!pair) return [];
      return [
        {
          key: `t${pair.id}`,
          kind: 'transformed' as const,
          pairId: pair.id,
          system: 1 as const,
          label: pair.sys1Point,
          n: position.n,
          e: position.e,
          status: getWeightStatus(pair.weight),
          flagged: false,
          ariaLabel: `Select transformed Record point ${pair.sys1Point}`,
        },
      ];
    }),
  ];
};
